import {ALBUMS_PATH} from './routePaths'

interface CampSwitchRule {
  /** Matched against the current pathname (no query string, no hash). */
  match: RegExp
  /** Where to send the user instead. */
  to: string
}

// `clientId` is never a URL parameter in this app — it lives only in AuthStore
// and localStorage — so every resource id in a route is implicitly scoped to
// "whatever camp is current". Switching camps therefore reinterprets an id
// against a client it does not belong to: AlbumManager refetches
// `getAlbum({clientID: <new>, ID: <old>})`, gets `notfound`, and replaces to
// `/albumnotfound`, which is not a registered route and falls through to the
// catch-all 404.
//
// Album ids are tenant-scoped by nature, so the right answer is to drop to the
// index rather than try to preserve and reinterpret the id.
//
// Scope is deliberately narrow: only routes listed here move. Anything absent
// leaves the user where they are, which is why `/microposts` (camp-agnostic) is
// untouched. Turning this into "always reset to /albums" would recreate the
// deep-link complaint this same work item was filed for.
//
// `/integrations/eztexting/groups/:id` has the identical defect and is knowingly
// omitted — see the Known Constraints in the AB#25795 plan. Adding it is one
// more entry in this table.
const CAMP_SWITCH_RULES: ReadonlyArray<CampSwitchRule> = [
  // `/album/<id>` and `/album/<id>/upload`. The segment boundary matters:
  // a bare `startsWith('/album')` would also swallow `/albums` itself and
  // navigate the index to the index on every camp switch.
  {match: /^\/album\/[^/]+(?:\/.*)?$/, to: ALBUMS_PATH},
]

/**
 * Where the user should be sent when they switch camps from `pathname`.
 *
 * @returns the fallback path, or `undefined` when the current route is valid
 * under any camp and the user should stay put.
 */
export const campSwitchFallback = (pathname: string): string | undefined => {
  if (typeof pathname !== 'string' || pathname.length === 0) {
    return undefined
  }

  return CAMP_SWITCH_RULES.find((rule) => rule.match.test(pathname))?.to
}
